import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  LogOut, 
  MessageSquare, 
  BookOpen, 
  Calendar,
  Camera,
  Heart,
  Clock,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  User,
  Bell
} from "lucide-react";
import { getFamilyDashboard } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { format, parseISO, isToday, isTomorrow, isPast } from "date-fns";
import { fr } from "date-fns/locale";

/**
 * ParentDashboardPage - Tableau de bord parent
 * Vue simplifiée du cartable pour les familles
 */
export default function ParentDashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const { data: dashboard } = await getFamilyDashboard();
      setData(dashboard);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      setError("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/parent");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-40 w-full rounded-2xl" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={() => navigate("/parent")}>
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-amber-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-400 rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-800 leading-tight">
                {data?.student_name || "Élève"}
              </p>
              <p className="text-xs text-slate-500">Espace famille</p>
            </div>
          </div>
          
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleLogout}
            className="text-slate-600"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Quick stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard 
            icon={MessageSquare} 
            label="Messages" 
            value={data?.messages?.length || 0}
            color="blue"
          />
          <StatCard 
            icon={BookOpen} 
            label="Devoirs" 
            value={data?.homework?.length || 0}
            color="green"
          />
          <StatCard 
            icon={Calendar} 
            label="Réunions" 
            value={data?.meetings?.length || 0}
            color="purple"
          />
          <StatCard 
            icon={Camera} 
            label="Albums" 
            value={data?.albums?.length || 0}
            color="amber"
          />
        </div>

        {/* Messages section */}
        <SectionCard
          icon={MessageSquare}
          title="Messages récents"
          color="blue"
          isEmpty={!data?.messages?.length}
          emptyText="Aucun message"
        >
          <div className="space-y-3">
            {data?.messages?.slice(0, 5).map((msg) => (
              <MessageItem key={msg.id} message={msg} />
            ))}
          </div>
        </SectionCard>

        {/* Homework section */}
        <SectionCard
          icon={BookOpen}
          title="Devoirs à faire"
          color="green"
          isEmpty={!data?.homework?.length}
          emptyText="Aucun devoir en attente"
        >
          <div className="space-y-3">
            {data?.homework?.slice(0, 5).map((hw) => (
              <HomeworkItem key={hw.id} homework={hw} />
            ))}
          </div>
        </SectionCard>

        {/* Life entries section */}
        <SectionCard
          icon={Heart}
          title="Cahier de vie"
          color="pink"
          isEmpty={!data?.life_entries?.length}
          emptyText="Aucune entrée récente"
        >
          <div className="space-y-3">
            {data?.life_entries?.slice(0, 5).map((entry) => (
              <LifeEntryItem key={entry.id} entry={entry} />
            ))}
          </div>
        </SectionCard>

        {/* Albums section */}
        {data?.albums?.length > 0 && (
          <SectionCard
            icon={Camera}
            title="Albums photos"
            color="amber"
          >
            <div className="grid grid-cols-2 gap-3">
              {data?.albums?.slice(0, 4).map((album) => (
                <AlbumCard key={album.id} album={album} />
              ))}
            </div>
          </SectionCard>
        )}

        {/* Meetings section */}
        {data?.meetings?.length > 0 && (
          <SectionCard
            icon={Calendar}
            title="Prochaines réunions"
            color="purple"
          >
            <div className="space-y-3">
              {data?.meetings?.slice(0, 3).map((meeting) => (
                <MeetingItem key={meeting.id} meeting={meeting} />
              ))}
            </div>
          </SectionCard>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-sm text-slate-500">
        <p>L'École de Navi - Espace Parents</p>
      </footer>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
    amber: "bg-amber-100 text-amber-600",
  };

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function SectionCard({ icon: Icon, title, color, children, isEmpty, emptyText }) {
  const colors = {
    blue: "text-blue-600",
    green: "text-green-600",
    purple: "text-purple-600",
    amber: "text-amber-600",
    pink: "text-pink-600",
  };

  return (
    <Card className="rounded-2xl shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className={`w-5 h-5 ${colors[color]}`} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <p className="text-sm text-slate-400 text-center py-4">{emptyText}</p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

function MessageItem({ message }) {
  const priorityColors = {
    normal: "bg-slate-100 text-slate-600",
    important: "bg-amber-100 text-amber-700",
    urgent: "bg-red-100 text-red-700",
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-medium text-slate-800 truncate">{message.title}</p>
          {message.priority !== "normal" && (
            <Badge className={`text-xs ${priorityColors[message.priority]}`}>
              {message.priority === "urgent" ? "Urgent" : "Important"}
            </Badge>
          )}
        </div>
        <p className="text-sm text-slate-600 line-clamp-2">{message.content}</p>
        <p className="text-xs text-slate-400 mt-1">
          {format(parseISO(message.published_at || message.created_at), "d MMMM", { locale: fr })}
        </p>
      </div>
      {message.requires_signature && !message.signed && (
        <Badge className="bg-amber-100 text-amber-700 text-xs">
          À signer
        </Badge>
      )}
    </div>
  );
}

function HomeworkItem({ homework }) {
  const dueDate = parseISO(homework.due_date);
  const isOverdue = isPast(dueDate) && !isToday(dueDate);
  
  let dateLabel = format(dueDate, "EEEE d MMMM", { locale: fr });
  if (isToday(dueDate)) dateLabel = "Aujourd'hui";
  if (isTomorrow(dueDate)) dateLabel = "Demain";

  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
      isOverdue ? "bg-red-50" : "bg-slate-50 hover:bg-slate-100"
    }`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isOverdue ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"
      }`}>
        <BookOpen className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-800">{homework.subject}</p>
        <p className="text-sm text-slate-600 line-clamp-2 mt-0.5">{homework.description}</p>
        <div className="flex items-center gap-2 mt-1">
          <Clock className="w-3 h-3 text-slate-400" />
          <p className={`text-xs ${isOverdue ? "text-red-600 font-medium" : "text-slate-400"}`}>
            {dateLabel}
          </p>
        </div>
      </div>
    </div>
  );
}

function LifeEntryItem({ entry }) {
  const typeLabels = {
    moment: "Moment de classe",
    sortie: "Sortie",
    projet: "Projet",
    evenement: "Événement",
    anniversaire: "Anniversaire",
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
      {entry.photos?.[0] ? (
        <img 
          src={entry.photos[0]} 
          alt="" 
          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-16 h-16 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
          <Heart className="w-6 h-6 text-pink-500" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-medium text-slate-800 truncate">{entry.title}</p>
          <Badge variant="outline" className="text-xs">
            {typeLabels[entry.type] || entry.type}
          </Badge>
        </div>
        <p className="text-sm text-slate-600 line-clamp-2">{entry.content}</p>
        <p className="text-xs text-slate-400 mt-1">
          {format(parseISO(entry.date), "d MMMM yyyy", { locale: fr })}
        </p>
      </div>
    </div>
  );
}

function AlbumCard({ album }) {
  const coverPhoto = album.photos?.[0];

  return (
    <div className="rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer">
      {coverPhoto ? (
        <img 
          src={coverPhoto.url} 
          alt={album.title}
          className="w-full h-24 object-cover"
        />
      ) : (
        <div className="w-full h-24 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
          <Camera className="w-8 h-8 text-amber-400" />
        </div>
      )}
      <div className="p-3">
        <p className="font-medium text-slate-800 text-sm truncate">{album.title}</p>
        <p className="text-xs text-slate-500">{album.photos?.length || 0} photos</p>
      </div>
    </div>
  );
}

function MeetingItem({ meeting }) {
  const meetingDate = parseISO(meeting.date);
  
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-purple-50">
      <div className="w-12 h-12 rounded-xl bg-purple-100 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-purple-700">
          {format(meetingDate, "d")}
        </span>
        <span className="text-[10px] text-purple-600 uppercase">
          {format(meetingDate, "MMM", { locale: fr })}
        </span>
      </div>
      <div className="flex-1">
        <p className="font-medium text-slate-800">{meeting.title || "Réunion"}</p>
        <p className="text-sm text-slate-600">{meeting.time}</p>
        {meeting.location && (
          <p className="text-xs text-slate-400">{meeting.location}</p>
        )}
      </div>
      {meeting.status === "confirmed" && (
        <CheckCircle2 className="w-5 h-5 text-green-500" />
      )}
    </div>
  );
}
